import React from "react";
import styled, { keyframes } from "styled-components";
import colors from "../../../constants/colors";

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const grow = keyframes`
  0% { transform: scaleY(0); }
  100% { transform: scaleY(1); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 0.8; }
`;

const EmptyChartContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const ChartWrapper = styled.div`
  width: 85%;
  height: 85%;
  max-width: 180px;
  max-height: 180px;
  display: flex;
  flex-direction: column;
  animation: ${float} 3s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ChartBars = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  gap: 8%;
  padding: 0 10%;
  position: relative;
  direction: rtl;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${colors.PURPLE};
  }
`;

const Bar = styled.div<{ $height: number; $delay: number }>`
  flex: 1;
  height: ${({ $height }) => $height}%;
  background: linear-gradient(180deg, ${colors.PURPLE_LIGHT} 0%, ${colors.PURPLE} 100%);
  border-radius: 4px 4px 0 0;
  transform-origin: bottom;
  animation: ${grow} 1s ease-out ${({ $delay }) => $delay}s both,
             ${pulse} 2s ease-in-out infinite ${({ $delay }) => $delay + 1}s;
  border: 2px solid ${colors.PURPLE};
  border-bottom: none;
  opacity: 0.6;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: scaleY(1);
  }
`;

const ChartTitle = styled.div`
  height: 15%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8%;
`;

const TitleLine = styled.div<{ $width: string; $delay: number }>`
  height: 3px;
  width: ${({ $width }) => $width};
  background: ${colors.PURPLE_LIGHT};
  border-radius: 2px;
  margin: 0 4px;
  animation: ${pulse} 2s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.7;
  }
`;

const EmptyChart: React.FC = () => {
  return (
    <EmptyChartContainer>
      <ChartWrapper>
        <ChartBars>
          <Bar $height={30} $delay={0} />
          <Bar $height={50} $delay={0.2} />
          <Bar $height={25} $delay={0.4} />
          <Bar $height={45} $delay={0.6} />
          <Bar $height={35} $delay={0.8} />
        </ChartBars>
        <ChartTitle>
          <TitleLine $width="30%" $delay={0} />
          <TitleLine $width="20%" $delay={0.3} />
          <TitleLine $width="25%" $delay={0.6} />
        </ChartTitle>
      </ChartWrapper>
    </EmptyChartContainer>
  );
};

export default EmptyChart;
